import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  useGetConnectionsQuery,
  useSyncCalendarMutation,
  useGetEventsQuery,
  useGetBriefingDetailMutation,
  useRegenerateBriefingMutation,
  useGetCrmStatusQuery,
} from '../../../api/preCallApi';
import { useGetBillingBalanceQuery } from '../../../api/billingApi';

// Cost in credits to view a pre-call intelligence dossier.
// Must match CREDIT_COSTS["recon_brief"] on the backend.
export const RECON_BRIEF_COST = 5;

export function usePreCallIntel() {
  const navigate = useNavigate();

  const { data: connectionsData, isLoading: loadingConns } = useGetConnectionsQuery();
  const { data: eventsData, isLoading: loadingEvents } = useGetEventsQuery();
  const [syncCalendar, { isLoading: isSyncing }] = useSyncCalendarMutation();
  const { data: crmStatusData } = useGetCrmStatusQuery();
  const { data: balanceData } = useGetBillingBalanceQuery();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'prospect' | 'company' | 'tactical' | 'battlecards'>('overview');

  // Briefing state is now managed manually — not auto-fetched.
  // The mutation is triggered only when the user explicitly clicks "View Intelligence".
  const [fetchBriefing, { isLoading: isMutationLoading }] = useGetBriefingDetailMutation();
  const [regenerateBriefing, { isLoading: isRegenerating }] = useRegenerateBriefingMutation();
  
  const [briefingDetail, setBriefingDetail] = useState<any>(null);
  const [briefingEventId, setBriefingEventId] = useState<string | null>(null);

  const [fetchingEventId, setFetchingEventId] = useState<string | null>(null);
  const [briefingCache, setBriefingCache] = useState<Record<string, any>>({});
  
  const loadingBriefing = (isMutationLoading || isRegenerating) && fetchingEventId === selectedEventId;

  const connections = connectionsData?.data || [];
  const events = eventsData?.data || [];
  const crmStatus = crmStatusData?.data;

  const displayedEvents = events.filter((e: any) => {
    const end = new Date(e.end_time);
    return end >= new Date();
  });

  const isSalesforceConnected = crmStatus?.salesforce?.connected;
  const isHubspotConnected = crmStatus?.hubspot?.connected;
  const isCrmConnected = Boolean(isSalesforceConnected || isHubspotConnected);
  const connectedCrmName = isSalesforceConnected ? 'Salesforce' : isHubspotConnected ? 'HubSpot' : null;

  const currentEvent = events.find((e: any) => e.id === selectedEventId);

  // Synchronize the currently selected event's briefing from cache
  useEffect(() => {
    if (selectedEventId && briefingCache[selectedEventId]) {
      setBriefingDetail(briefingCache[selectedEventId]);
      setBriefingEventId(selectedEventId);
    }
  }, [selectedEventId, briefingCache]);

  // Clear briefing detail when a different event is selected
  const handleSelectEvent = (eventId: string) => {
    if (eventId !== selectedEventId) {
      setSelectedEventId(eventId);
      setActiveTab('overview');
      
      if (!briefingCache[eventId]) {
        setBriefingDetail(null);
        setBriefingEventId(null);
        
        // Auto-fetch if the event already has a briefing generated
        const eventObj = events.find((e: any) => e.id === eventId);
        if (eventObj?.has_briefing) {
          setFetchingEventId(eventId);
          fetchBriefing(eventId).unwrap().then(res => {
            setBriefingCache(prev => ({ ...prev, [eventId]: res.data }));
          }).catch(err => {
            console.error("Failed to fetch existing briefing:", err);
            setBriefingCache(prev => ({ ...prev, [eventId]: null }));
            Swal.fire({
              icon: 'error',
              title: 'Brief Generation Failed',
              text: err?.data?.message || 'Could not retrieve the briefing from the AI service.',
              background: 'var(--admin-card-bg)',
              color: 'var(--admin-card-text)',
            });
          }).finally(() => setFetchingEventId(null));
        } else {
          setFetchingEventId(null);
        }
      }
    }
  };

  // On initial load or when events change, if the first auto-selected event has a briefing, fetch it
  useEffect(() => {
    if (selectedEventId && currentEvent?.has_briefing && !briefingDetail && !isMutationLoading && briefingEventId !== selectedEventId && !fetchingEventId && !(selectedEventId in briefingCache)) {
      setFetchingEventId(selectedEventId);
      fetchBriefing(selectedEventId).unwrap().then(res => {
        setBriefingCache(prev => ({ ...prev, [selectedEventId]: res.data }));
      }).catch(err => {
        console.error("Failed to auto-fetch existing briefing:", err);
        setBriefingCache(prev => ({ ...prev, [selectedEventId]: null }));
        Swal.fire({
          icon: 'error',
          title: 'Brief Generation Failed',
          text: err?.data?.message || 'Could not retrieve the briefing from the AI service.',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)',
        });
      }).finally(() => setFetchingEventId(null));
    }
  }, [selectedEventId, currentEvent, briefingDetail, isMutationLoading, briefingEventId, fetchBriefing, fetchingEventId, briefingCache]);

  /**
   * Called when the user clicks "View Intelligence Dossier".
   * Shows a credit cost confirmation, then fires the mutation.
   */
  const handleViewIntelligence = async (eventId: string) => {
    const credits = balanceData?.data?.balance || 0;

    if (credits < RECON_BRIEF_COST) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Insufficient Credits',
        text: `You have ${credits} credits, but generating this brief requires ${RECON_BRIEF_COST} credits.`,
        showCancelButton: true,
        confirmButtonText: 'Go to Billing',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3E8FA6',
        cancelButtonColor: '#64748b',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
      });

      if (result.isConfirmed) {
        navigate('/admin/billing');
      }
      return;
    }

    const result = await Swal.fire({
      icon: 'info',
      title: 'View Intelligence Dossier',
      html: `
        <div style="display: flex; flex-direction: column; gap: 16px; text-align: left; margin-top: 12px;">
          <p style="font-size: 14px; line-height: 1.6; color: var(--admin-card-text); margin: 0;">
            FinnAI will cross-reference your calendar data${isCrmConnected ? ', CRM records,' : ''} and competitive web intelligence to compile a comprehensive pre-call brief.
          </p>
          
          ${!isCrmConnected ? `
          <div style="display: flex; align-items: flex-start; gap: 12px; background: rgba(245, 158, 11, 0.05); border-left: 3px solid #d97706; padding: 12px 16px;">
            <svg style="flex-shrink: 0; margin-top: 2px;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>
            <div>
              <p style="margin: 0; font-size: 12px; color: #d97706; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">CRM DISCONNECTED</p>
              <p style="margin: 4px 0 0 0; font-size: 13px; color: #b45309; line-height: 1.5;">
                Salesforce or HubSpot is not linked. This brief will rely solely on calendar and public web intelligence.
              </p>
            </div>
          </div>
          ` : ''}

          <div style="display: flex; align-items: center; justify-content: space-between; border-top: 1px solid var(--admin-card-border); padding-top: 16px; margin-top: 4px;">
            <span style="font-size: 13px; color: var(--admin-card-subtext); font-weight: 600;">Generation Cost</span>
            <div style="
              display: inline-flex; align-items: center; gap: 6px;
              background: rgba(62, 143, 166, 0.08); border: 1px solid rgba(62, 143, 166, 0.3);
              padding: 6px 12px; font-size: 13px; font-weight: 700; color: #3E8FA6;
            ">
              <span>⚡</span> <span>${RECON_BRIEF_COST} Credits</span>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: `Use ${RECON_BRIEF_COST} Credits & Generate`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3E8FA6',
      cancelButtonColor: '#64748b',
      background: 'var(--admin-card-bg)',
      color: 'var(--admin-card-text)',
    });

    if (!result.isConfirmed) return;

    try {
      setFetchingEventId(eventId);
      const response = await fetchBriefing(eventId).unwrap();
      setBriefingCache(prev => ({ ...prev, [eventId]: response.data }));
      setBriefingDetail(response.data);
      setBriefingEventId(eventId);
      
      // Dispatch floating credit animation event
      window.dispatchEvent(new CustomEvent('spend-credits', { detail: { amount: RECON_BRIEF_COST } }));
    } catch (err: any) {
      // 402 errors are handled globally by the apiSlice interceptor.
      // For any other errors, show a local error message.
      if (err?.status !== 402) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Generate Brief',
          text: err?.data?.message || 'Something went wrong. Please try again.',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)',
        });
      }
    } finally {
      setFetchingEventId(null);
    }
  };

  const handleRegenerateIntelligence = async (eventId: string) => {
    const credits = balanceData?.data?.balance || 0;

    if (credits < RECON_BRIEF_COST) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Insufficient Credits',
        text: `You have ${credits} credits, but regenerating this brief requires ${RECON_BRIEF_COST} credits.`,
        showCancelButton: true,
        confirmButtonText: 'Go to Billing',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3E8FA6',
        cancelButtonColor: '#64748b',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
      });

      if (result.isConfirmed) {
        navigate('/admin/billing');
      }
      return;
    }

    const result = await Swal.fire({
      icon: 'info',
      title: 'Regenerate Intelligence Dossier',
      html: `
        <div style="display: flex; flex-direction: column; gap: 16px; text-align: left; margin-top: 12px;">
          <p style="font-size: 14px; line-height: 1.6; color: var(--admin-card-text); margin: 0;">
            This will fetch the latest Calendar and CRM data and regenerate the AI intelligence brief.
          </p>
          <div style="display: flex; align-items: center; justify-content: space-between; border-top: 1px solid var(--admin-card-border); padding-top: 16px; margin-top: 4px;">
            <span style="font-size: 13px; color: var(--admin-card-subtext); font-weight: 600;">Regeneration Cost</span>
            <div style="
              display: inline-flex; align-items: center; gap: 6px;
              background: rgba(62, 143, 166, 0.08); border: 1px solid rgba(62, 143, 166, 0.3);
              padding: 6px 12px; font-size: 13px; font-weight: 700; color: #3E8FA6;
            ">
              <span>⚡</span> <span>${RECON_BRIEF_COST} Credits</span>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: `Use ${RECON_BRIEF_COST} Credits & Regenerate`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3E8FA6',
      cancelButtonColor: '#64748b',
      background: 'var(--admin-card-bg)',
      color: 'var(--admin-card-text)',
    });

    if (!result.isConfirmed) return;

    try {
      setFetchingEventId(eventId);
      const response = await regenerateBriefing(eventId).unwrap();
      setBriefingCache(prev => ({ ...prev, [eventId]: response.data }));
      setBriefingDetail(response.data);
      setBriefingEventId(eventId);
      
      window.dispatchEvent(new CustomEvent('spend-credits', { detail: { amount: RECON_BRIEF_COST } }));
      
      Swal.fire({
        icon: 'success',
        title: 'Regeneration Complete',
        text: 'The briefing has been updated with the latest data.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)'
      });
    } catch (err: any) {
      if (!briefingCache[eventId]) {
        setBriefingCache(prev => ({ ...prev, [eventId]: null }));
      }
      if (err?.status !== 402) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Regenerate Brief',
          text: err?.data?.message || 'Something went wrong. Please try again.',
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)',
        });
      }
    } finally {
      setFetchingEventId(null);
    }
  };

  const handleSyncNow = async () => {
    if (connections.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Calendars Connected',
        text: 'Please connect a Google or Outlook calendar account in the Integrations hub before syncing.',
        confirmButtonText: 'Go to Integrations',
        showCancelButton: true,
        confirmButtonColor: '#3E8FA6',
        cancelButtonColor: '#64748b',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/admin/integrations');
        }
      });
      return;
    }

    try {
      await syncCalendar().unwrap();
      Swal.fire({
        icon: 'success',
        title: 'Sync Complete',
        text: 'Successfully fetched upcoming sales meetings!',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (err: any) {
      Swal.fire('Sync Error', err?.data?.message || 'Failed to sync calendar meetings.', 'error');
    }
  };

  return {
    navigate,
    connections,
    loadingConns,
    events,
    displayedEvents,
    loadingEvents,
    briefingDetail,
    briefingEventId,
    loadingBriefing,
    crmStatus,
    isCrmConnected,
    connectedCrmName,
    isSalesforceConnected,
    isHubspotConnected,
    isSyncing,
    selectedEventId,
    setSelectedEventId: handleSelectEvent,
    activeTab,
    setActiveTab,
    handleSyncNow,
    handleViewIntelligence,
    handleRegenerateIntelligence,
  };
}
